import SEO from 'global/SEO';
import { useMemo, useState } from 'react';
import QueryProvider from 'global/QueryProvider';
import GuestRegisterModal from '../GuestRegisterModal';
import { ChartWrapper, HeroWrapper } from './Hero/styled';
import Header from './Hero/Header';
import { ContentContainer, ContentContainerLeft, ContentContainerRight, DetailContainer } from './Detail/styled';
import CouponDetail from './Detail/CouponDetail';
import About from './Detail/About';
import { CompanyFRFinItem } from '../../../../@types/company';
import ChartHeader from './Hero/ChartHeader';
import { CompanyChartType } from './types';
import { ChartContainer, MainChartContent } from '../Chart/styled';
import MainChart from './Hero/MainChart';

interface GuestFRCompanyProps {
  symbol: string;
  company: any;
  chart?: CompanyChartType;
  finItem?: CompanyFRFinItem;
}

const features = [
  {
    name: 'Bandar Detector',
    icon: 'icon-feature_bandar',
  },
  {
    name: 'Foreign Flow Chart',
    icon: 'icon-feature_foreign',
  },
  { name: 'Valuation Tool', icon: 'icon-feature_valuation' },
  { name: 'Stock Screener', icon: 'icon-feature_screener' },
  { name: 'Price-Alert', icon: 'icon-feature_pricealert' },
  { name: 'Real-time Charting Platform', icon: 'icon-feature_charting' },
];

const GuestFRCompany = ({
  symbol,
  company,
  chart,
  finItem,
}: GuestFRCompanyProps) => {
  const [showModalRegis, setShowModalRegis] = useState(false);

  const { name } = company;
  const metaTitle = `Saham: ${symbol.toUpperCase()} - PT. ${name} | Stockbit`;
  const metaDescription = `Informasi detail tentang saham ${symbol.toUpperCase()} - PT. ${name} hari ini, mulai dari harga, grafik, data historis dan profil lengkap lainnya.`;

  const generateSchemaJson = () => {
    const { name: companyName, exchange } = company;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://stockbit.com/symbol/${symbol}`,
      },
      headline: `Saham ${symbol} - PT. ${companyName} | Stockbit`,
      image: '',
      author: {
        '@type': 'Organization',
        name: 'Stockbit',
        url: 'https://stockbit.com',
      },
      mentions: [
        {
          '@type': 'Corporation',
          Legalname: `PT. ${companyName}`,
          tickerSymbol: `${exchange}:${symbol}`,
          url: `https://stockbit.com/symbol/${symbol}`,
        },
      ],
      description: `Informasi detail tentang saham ${symbol} - PT. ${companyName} hari ini, mulai dari harga, grafik, data historis dan profil lengkap lainnya.`,
    };
  };

  const generateMetaKeywords = () => {
    const symbolKeywords = `${symbol}, saham ${symbol}, ${symbol} saham, idx ${symbol}, ${symbol} idx, harga saham ${symbol}, harga saham ${symbol} hari ini, saham ${symbol} hari ini, ${symbol} saham hari ini, laporan keuangan ${symbol}, grafik saham ${symbol}, dividen ${symbol}, ${symbol} stock, ${symbol} hari ini, ${symbol} saham apa, analisa saham ${symbol}, saham ${symbol} bergerak di bidang apa, berita saham ${symbol}, saham ${symbol} chart, pemegang saham ${symbol}, analisis saham ${symbol}`;

    const companyKeywords = `saham ${name}, harga saham ${name}, harga saham ${name} hari ini, ${name} saham, harga 1 lot saham ${name}, grafik saham ${name}, cara beli saham ${name}, beli saham ${name}, kode saham ${name}, saham ${name} bergerak di bidang, investasi saham ${name}`;

    return `${symbolKeywords}, ${companyKeywords}`;
  };

  const about = useMemo(() => {
    if (company) {
      return JSON.parse(company.metadata.find((item) => item.key === 'about').value);
    }
    return [];
  }, [company]);

  const issuer = useMemo(() => {
    if (company) {
      return company.metadata.find((item) => item.key === 'issuer').value;
    }
    return [];
  }, [company]);

  return (
    <QueryProvider>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={generateMetaKeywords()}
        schemaJson={generateSchemaJson()}
      />

      <div style={{ width: '100%' }}>
        <HeroWrapper>
          <Header
            symbol={symbol}
            company={company}
            isGuestCompany
            onClickBuyButton={() => setShowModalRegis(true)}
            onExpandToggle={() => setShowModalRegis(true)}
            onClickLink={() => setShowModalRegis(true)}
          />
          <ChartHeader
            onExpand={() => setShowModalRegis(true)}
            onSelectTimeframe={() => setShowModalRegis(true)}
            timeframe="1y"
            chartType="yield"
            onSelectChartType={() => setShowModalRegis(true)}
            isChartVisible
            priceInfo={{
              bidPrice: finItem?.bid_price,
              offerPrice: finItem?.offer_price,
              yield: finItem?.yield,
            }}
          />
          <ChartWrapper>
            <MainChartContent>
              <ChartContainer>
                <MainChart
                  disableChartbitLink
                  isLoadingChart={false}
                  symbol={symbol}
                  visible
                  chart={chart}
                  timeframe="1y"
                  chartType="yield"
                  onClickChart={() => setShowModalRegis(true)}
                />
              </ChartContainer>
            </MainChartContent>
          </ChartWrapper>
        </HeroWrapper>
        <DetailContainer>
          <ContentContainer style={{ border: 'none' }}>
            <ContentContainerLeft>
              <CouponDetail
                annualRate={finItem.annual_coupon_rate}
                couponDistribution={finItem.coupon_distribution}
                dueDate={finItem.due_date}
                issuedDate={finItem.issued_date}
                minimumOrder={finItem.minimum_order}
                nextDate={finItem.next_coupon_date}
                issuer={issuer}
              />
            </ContentContainerLeft>
            <ContentContainerRight>
              <About data={about} />
            </ContentContainerRight>
          </ContentContainer>
        </DetailContainer>
      </div>
      <GuestRegisterModal
        isCompanyStock
        visible={showModalRegis}
        setVisible={setShowModalRegis}
        features={features}
      />
    </QueryProvider>
  );
};

export default GuestFRCompany;
